import React, { useState, useEffect } from 'react';
import { Class, GradeLevel, GradingSystem } from '../../types';
import { XMarkIcon, InformationCircleIcon } from './Icons';

import Spinner from './Spinner';

/** Năm học hiện tại (VN: tháng 9 chuyển sang năm mới) */
const getCurrentAcademicYear = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // 1-12
  if (month >= 9) return `${year}-${year + 1}`;
  return `${year - 1}-${year}`;
};

interface ClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (classData: Omit<Class, 'id' | 'studentIds'>) => Promise<void> | void;
  classToEdit?: Class | null;
  isSaving?: boolean;
}

const ClassModal: React.FC<ClassModalProps> = ({ isOpen, onClose, onSave, classToEdit, isSaving }) => {
  const [name, setName] = useState('');
  const [academicYear, setAcademicYear] = useState(getCurrentAcademicYear);
  const [gradeLevel, setGradeLevel] = useState<Class['gradeLevel']>(GradeLevel.TIEU_HOC);
  const [classType, setClassType] = useState<Class['classType']>('physical');
  const [allowJoinFromOutside, setAllowJoinFromOutside] = useState(true);
  const [location, setLocation] = useState('');
  const [useSecurityQuestion, setUseSecurityQuestion] = useState(false);
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [gradingSystem, setGradingSystem] = useState<GradingSystem>('numeric');
  const [defaultParticipationScore, setDefaultParticipationScore] = useState<number>(10);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (classToEdit) {
      setName(classToEdit.name);
      setAcademicYear(classToEdit.academicYear);
      setGradeLevel(classToEdit.gradeLevel);
      setClassType(classToEdit.classType);
      setAllowJoinFromOutside(classToEdit.allowJoinFromOutside ?? (classToEdit.enrollmentMode !== 'manual'));
      setLocation(classToEdit.location || '');
      const hasSec = !!(classToEdit.securityQuestion && classToEdit.securityQuestion.trim());
      setUseSecurityQuestion(hasSec);
      setSecurityQuestion(classToEdit.securityQuestion || '');
      setSecurityAnswer('');
      setIsLocked(!!classToEdit.isLocked);
      setGradingSystem(classToEdit.gradingSystem || 'numeric');
      setDefaultParticipationScore(classToEdit.defaultParticipationScore ?? 10);
    } else {
      setName('');
      setAcademicYear(getCurrentAcademicYear());
      setGradeLevel(GradeLevel.TIEU_HOC);
      setClassType('physical');
      setAllowJoinFromOutside(true);
      setLocation('');
      setUseSecurityQuestion(false);
      setSecurityQuestion('');
      setSecurityAnswer('');
      setIsLocked(false);
      setGradingSystem('numeric');
      setDefaultParticipationScore(10);
    }
    setErrors({});
  }, [classToEdit, isOpen]);

  const validate = (): boolean => {
    const err: Record<string, string> = {};
    if (!name.trim()) err.name = 'Vui lòng nhập tên lớp học';
    if (!academicYear.trim()) err.academicYear = 'Vui lòng nhập năm học';
    if (useSecurityQuestion) {
      if (!securityQuestion.trim()) err.securityQuestion = 'Vui lòng nhập câu hỏi bảo mật';
      if (!securityAnswer.trim()) err.securityAnswer = 'Vui lòng nhập đáp án';
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSave = async () => {
    if (validate()) {
      await onSave({ 
        name, 
        academicYear, 
        gradeLevel, 
        classType, 
        allowJoinFromOutside,
        location: location.trim() || undefined,
        securityQuestion: useSecurityQuestion ? securityQuestion.trim() || undefined : undefined,
        securityAnswer: useSecurityQuestion ? securityAnswer.trim() || undefined : undefined,
        isLocked,
        gradingSystem,
        defaultParticipationScore,
      });
      // onClose() will be handled by parent on success, 
      // or we stay open on error to let user fix and retry.
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto py-10" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full m-4 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex justify-between items-center bg-teal-600 text-white rounded-t-2xl">
          <h3 className="text-xl font-bold">{classToEdit ? 'Cập nhật Lớp học' : 'Thiết lập Lớp học mới'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors"><XMarkIcon className="h-6 w-6 text-white" /></button>
        </div>
        
        <div className="p-6 space-y-6 overflow-y-auto">
          {/* Hình thức học tập */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-xs font-semibold text-slate-500">Hình thức giảng dạy</label>
              <div className="group relative">
                <InformationCircleIcon className="h-4 w-4 text-slate-300 cursor-help hover:text-teal-500 transition-colors" />
                <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-slate-800 text-white text-[11px] rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-all pointer-events-none z-50 leading-relaxed font-normal transform translate-y-2 group-hover:translate-y-0">
                  <p className="mb-1"><span className="text-teal-400 font-bold">Tại chỗ:</span> Học trực tiếp tại trường/trung tâm.</p>
                  <p className="mb-1"><span className="text-teal-400 font-bold">Trực tuyến:</span> Học qua Zoom, Google Meet...</p>
                  <p><span className="text-teal-400 font-bold">Hybrid:</span> Kết hợp cả trực tiếp và online.</p>
                  <div className="absolute top-full left-2 border-8 border-transparent border-t-slate-800"></div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { val: 'physical', label: 'Tại chỗ', sub: 'Trường/Trung tâm' },
                { val: 'virtual', label: 'Trực tuyến', sub: 'Học qua Zoom/Meet' },
                { val: 'hybrid', label: 'Hybrid', sub: 'Cả trực tiếp & online' },
              ].map(opt => (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => setClassType(opt.val as any)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${classType === opt.val ? 'border-teal-500 bg-teal-50 ring-4 ring-teal-100' : 'border-slate-100 hover:border-teal-200 bg-slate-50/50'}`}
                >
                  <p className={`text-sm font-bold ${classType === opt.val ? 'text-teal-700' : 'text-slate-600'}`}>{opt.label}</p>
                  <p className="text-[10px] font-medium text-slate-400 mt-0.5">{opt.sub}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Cho phép tham gia từ ngoài (on/off) */}
          <div className="p-4 bg-sky-50/50 border border-sky-100 rounded-xl">
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-700">Cho phép HS/PH tìm lớp bằng mã và gửi yêu cầu tham gia</span>
                <div className="group relative flex-shrink-0">
                  <InformationCircleIcon className="h-4 w-4 text-slate-300 cursor-help hover:text-sky-500 transition-colors" />
                  <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 w-72 p-3 bg-slate-800 text-white text-[11px] rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-[100] leading-relaxed font-normal whitespace-normal">
                    <p className="mb-1.5"><span className="text-sky-400 font-bold">Bật:</span> Học sinh/phụ huynh có mã lớp có thể tìm thấy và gửi yêu cầu. GV phê duyệt để thêm vào lớp.</p>
                    <p><span className="text-slate-400 font-bold">Tắt:</span> Dù có mã, user tìm lớp cũng không thấy (bị từ chối). Chỉ GV thêm học sinh thủ công.</p>
                    <div className="absolute left-full top-1/2 -translate-y-1/2 border-8 border-transparent border-l-slate-800"></div>
                  </div>
                </div>
              </div>
              <div className={`relative w-12 h-6 rounded-full transition-colors ${allowJoinFromOutside ? 'bg-teal-500' : 'bg-slate-300'}`}>
                <input
                  type="checkbox"
                  checked={allowJoinFromOutside}
                  onChange={e => setAllowJoinFromOutside(e.target.checked)}
                  className="sr-only"
                />
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${allowJoinFromOutside ? 'left-7' : 'left-1'}`} />
              </div>
            </label>
            <p className="text-[10px] text-slate-500 mt-2">Mọi lớp đều có mã định danh. HS đăng nhập bằng mã 8 ký tự + ngày sinh.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Tên Lớp học *</label>
              <input type="text" value={name} onChange={e => { setName(e.target.value); setErrors(prev => ({ ...prev, name: '' })); }} className={`w-full p-3 bg-slate-50 border-2 rounded-xl focus:border-teal-500 focus:bg-white transition-all font-bold text-sm ${errors.name ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`} placeholder="Ví dụ: Lớp Toán 5A" />
              {errors.name && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Năm học *</label>
              <input type="text" value={academicYear} onChange={e => { setAcademicYear(e.target.value); setErrors(prev => ({ ...prev, academicYear: '' })); }} className={`w-full p-3 bg-slate-50 border-2 rounded-xl focus:border-teal-500 focus:bg-white transition-all font-bold text-sm ${errors.academicYear ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`} />
              {errors.academicYear && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.academicYear}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Cấp học</label>
              <select value={gradeLevel} onChange={e => setGradeLevel(e.target.value as GradeLevel)} className="w-full p-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 transition-all font-bold text-sm outline-none">
                {Object.values(GradeLevel).map(level => <option key={level} value={level}>{level}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Kiểu chấm điểm mặc định</label>
              <select value={gradingSystem} onChange={e => setGradingSystem(e.target.value as GradingSystem)} className="w-full p-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 transition-all font-bold text-sm outline-none">
                <option value="numeric">Hệ 10 (0-10)</option>
                <option value="letter">Hệ chữ (A, B, C, D, F)</option>
                <option value="pass_fail">Hệ Đạt / Chưa đạt</option>
              </select>
            </div>
          </div>

          <div className="p-4 bg-teal-50 border border-teal-100 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-bold text-teal-800">Cơ chế điểm chuyên cần thông minh</label>
              <div className="group relative">
                <InformationCircleIcon className="h-4 w-4 text-teal-400 cursor-help hover:text-teal-600 transition-colors" />
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-3 bg-slate-800 text-white text-[11px] rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-all pointer-events-none z-50 leading-relaxed font-normal transform translate-y-2 group-hover:translate-y-0">
                  <p className="mb-1.5 font-bold text-teal-400">Tăng tương tác cho lớp học!</p>
                  <p>Hệ thống sẽ <span className="text-teal-300 font-bold">tự động chấm điểm</span> này cho HS/PH khi họ vào xem và xác nhận các "Nhiệm vụ" (không cần nộp minh chứng).</p>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-slate-800"></div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-grow">
                <p className="text-[10px] text-teal-600 font-medium mb-2">Mức điểm thưởng cho mỗi nhiệm vụ "Chỉ xem/Xác nhận":</p>
                <div className="flex items-center gap-3">
                  <input 
                    type="range" 
                    min="0" 
                    max="10" 
                    step="0.5"
                    value={defaultParticipationScore} 
                    onChange={e => setDefaultParticipationScore(parseFloat(e.target.value))}
                    className="flex-grow h-2 bg-teal-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                  />
                  <span className="w-12 text-center font-black text-teal-700 bg-white border border-teal-200 rounded-lg px-2 py-1 text-sm shadow-sm">{defaultParticipationScore}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">{classType === 'virtual' ? 'Link phòng học' : 'Phòng học / Địa điểm'}</label>
              <input type="text" value={location} onChange={e => setLocation(e.target.value)} className="w-full p-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 focus:bg-white transition-all font-bold text-sm" placeholder={classType === 'virtual' ? 'https://zoom.us/j/...' : 'Phòng 302, Tòa nhà A'} />
            </div>
          </div>

          <div className="p-4 bg-amber-50/50 border border-amber-100 rounded-xl space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={useSecurityQuestion} onChange={e => { setUseSecurityQuestion(e.target.checked); if (!e.target.checked) { setSecurityQuestion(''); setSecurityAnswer(''); } }} className="h-4 w-4 rounded border-amber-300 text-amber-600" />
              <span className="text-sm font-bold text-amber-800">Bật câu hỏi bảo mật khi đăng nhập</span>
            </label>
            <p className="text-[10px] text-amber-600">Khi bật: HS phải trả lời đúng câu hỏi mới đăng nhập được (bảo vệ khi bị lộ thông tin)</p>
            {useSecurityQuestion && (
              <>
                <div>
                  <label className="block text-[10px] font-medium text-amber-700 mb-1">Câu hỏi bảo mật *</label>
                  <input type="text" value={securityQuestion} onChange={e => { setSecurityQuestion(e.target.value); setErrors(prev => ({ ...prev, securityQuestion: '' })); }} className={`w-full p-3 bg-white border-2 rounded-xl focus:border-amber-500 transition-all text-sm ${errors.securityQuestion ? 'border-rose-300 bg-rose-50/30' : 'border-amber-100'}`} placeholder="VD: Tên trường con đang học?" required={useSecurityQuestion} />
                  {errors.securityQuestion && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.securityQuestion}</p>}
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-amber-700 mb-1">Đáp án *</label>
                  <input type="text" value={securityAnswer} onChange={e => { setSecurityAnswer(e.target.value); setErrors(prev => ({ ...prev, securityAnswer: '' })); }} className={`w-full p-3 bg-white border-2 rounded-xl focus:border-amber-500 transition-all text-sm ${errors.securityAnswer ? 'border-rose-300 bg-rose-50/30' : 'border-amber-100'}`} placeholder={classToEdit && classToEdit.securityQuestion ? 'Nhập mới để đổi đáp án' : 'Đáp án câu hỏi bảo mật'} required={useSecurityQuestion} />
                  {errors.securityAnswer && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.securityAnswer}</p>}
                </div>
              </>
            )}
            <label className="flex items-center gap-2 cursor-pointer pt-2 border-t border-amber-100">
              <input type="checkbox" checked={isLocked} onChange={e => setIsLocked(e.target.checked)} className="h-4 w-4 rounded border-amber-300 text-amber-600" />
              <span className="text-sm font-bold text-amber-800">Khóa lớp tạm thời</span>
            </label>
            <p className="text-[10px] text-amber-600">Khi khóa: HS không thể đăng nhập bằng mã</p>
          </div>
        </div>

        <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 rounded-b-2xl border-t">
          <button onClick={onClose} disabled={isSaving} className="px-6 py-2 bg-white border border-slate-200 text-slate-500 font-bold text-sm rounded-xl hover:bg-slate-100 transition-all">Hủy</button>
          <button onClick={handleSave} className="flex items-center gap-2 px-10 py-2 text-sm font-bold text-white bg-teal-600 rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-100 active:scale-95 disabled:opacity-50 transition-all" disabled={!name.trim() || !academicYear.trim() || isSaving || (useSecurityQuestion && (!securityQuestion.trim() || !securityAnswer.trim()))}>
            {isSaving && <Spinner className="h-4 w-4" />}
            <span>{classToEdit ? 'Cập nhật' : 'Khởi tạo ngay'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClassModal;
